import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import mapboxgl from 'mapbox-gl'
import {
  YIELD_LAYER_ID,
  createYieldFillColorExpression,
  createYieldVectorTileSource,
  getYieldField,
  formatYieldValue,
} from '../../../../molecules/AnalysisMap/utils/yieldLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'
import { API_BASE_URL } from '../../../../../config'

const YIELD_FILL_LAYER_ID = YIELD_LAYER_ID
const YIELD_OUTLINE_LAYER_ID = `${YIELD_LAYER_ID}-building-outlines`
const YIELD_SOURCE_ID = `${YIELD_LAYER_ID}-source`
const YIELD_BUILDING_SOURCE_ID = `${YIELD_LAYER_ID}-buildings-source`

interface UseYieldLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

export const useYieldLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseYieldLayersProps) => {
  const { t } = useTranslation('ui')

  // Handle yield hexagon layer and building outlines
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const activeSection = (otherMapOptions as any).activeSection
    const isYieldSection = activeSection === 'valuation'
    const layerStates = (otherMapOptions as any).demographicsLayerStates

    if (!isYieldSection || !layerStates) {
      // Cleanup: remove yield layers and sources
      const layersToRemove = [YIELD_FILL_LAYER_ID, YIELD_OUTLINE_LAYER_ID]
      layersToRemove.forEach((id) => {
        if (map.current!.getLayer(id)) {
          try {
            map.current!.removeLayer(id)
          } catch {
            // ignore
          }
        }
      })
      const sourcesToRemove = [YIELD_SOURCE_ID, YIELD_BUILDING_SOURCE_ID]
      sourcesToRemove.forEach((id) => {
        if (map.current!.getSource(id)) {
          try {
            map.current!.removeSource(id)
          } catch {
            // ignore
          }
        }
      })
      return
    }

    const layerConfig = getLayerConfig(YIELD_LAYER_ID)
    const layerState = layerStates[YIELD_LAYER_ID] || {
      enabled: layerConfig?.defaultEnabled || true,
      opacity: layerConfig?.defaultOpacity || 50,
    }

    // Add hexagon vector tile source
    if (!map.current!.getSource(YIELD_SOURCE_ID)) {
      const sourceConfig = createYieldVectorTileSource(API_BASE_URL)
      map.current!.addSource(YIELD_SOURCE_ID, sourceConfig)
    }

    // Add hexagon fill layer
    if (!map.current!.getLayer(YIELD_FILL_LAYER_ID)) {
      const fillColorExpression = createYieldFillColorExpression(layerConfig)

      map.current!.addLayer({
        id: YIELD_FILL_LAYER_ID,
        type: 'fill',
        source: YIELD_SOURCE_ID,
        'source-layer': 'aggs',
        layout: {
          visibility: layerState.enabled ? 'visible' : 'none',
        },
        paint: {
          'fill-outline-color': 'transparent',
          'fill-color': fillColorExpression,
          'fill-opacity': layerState.opacity / 100,
        },
      })
    } else {
      map.current!.setLayoutProperty(
        YIELD_FILL_LAYER_ID,
        'visibility',
        layerState.enabled ? 'visible' : 'none'
      )
      map.current!.setPaintProperty(
        YIELD_FILL_LAYER_ID,
        'fill-opacity',
        layerState.opacity / 100
      )
    }

    // Add building outline source (existing building vector tile endpoint)
    if (!map.current!.getSource(YIELD_BUILDING_SOURCE_ID)) {
      map.current!.addSource(YIELD_BUILDING_SOURCE_ID, {
        type: 'vector',
        tiles: [
          `${API_BASE_URL}/buildings/vector-tile/geo_polygon_wkt/{z}/{x}/{y}`,
        ],
        minzoom: 13,
        maxzoom: 16,
      })
    }

    // Add building outline layer (line only, no fill)
    if (!map.current!.getLayer(YIELD_OUTLINE_LAYER_ID)) {
      map.current!.addLayer({
        id: YIELD_OUTLINE_LAYER_ID,
        type: 'line',
        source: YIELD_BUILDING_SOURCE_ID,
        'source-layer': 'hits',
        minzoom: 14,
        layout: {
          visibility: layerState.enabled ? 'visible' : 'none',
        },
        paint: {
          'line-color': '#555555',
          'line-width': 1,
          'line-opacity': 0.6,
        },
      })
    } else {
      map.current!.setLayoutProperty(
        YIELD_OUTLINE_LAYER_ID,
        'visibility',
        layerState.enabled ? 'visible' : 'none'
      )
    }
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Hover tooltip for yield hexagons
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const activeSection = (otherMapOptions as any).activeSection
    if (activeSection !== 'valuation') return

    const layerState = otherMapOptions.demographicsLayerStates?.[YIELD_LAYER_ID]
    if (!layerState?.enabled || !map.current!.getLayer(YIELD_FILL_LAYER_ID))
      return

    const field = getYieldField()

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: [YIELD_FILL_LAYER_ID],
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const value = feature.properties?.[field]
        const formattedValue = formatYieldValue(value)
        const layerName = t('analysisMap.layers.yieldHexagon', {
          defaultValue: 'Area Yield',
        })

        map.current!.getCanvas().style.cursor = 'pointer'

        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="padding: 8px 12px; font-size: 13px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${layerName}</div>
              <div>${formattedValue}</div>
            </div>`
          )
          .addTo(map.current!)
      } else {
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    const handleMouseLeave = () => {
      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    map.current.on('mousemove', YIELD_FILL_LAYER_ID, handleMouseMove)
    map.current.on('mouseleave', YIELD_FILL_LAYER_ID, handleMouseLeave)

    return () => {
      if (map.current) {
        map.current.off('mousemove', YIELD_FILL_LAYER_ID, handleMouseMove)
        map.current.off('mouseleave', YIELD_FILL_LAYER_ID, handleMouseLeave)
      }
      popup.current?.remove()
    }
  }, [mapLoaded, otherMapOptions.demographicsLayerStates, t])
}
